import { cn } from "@/lib/utils";
import dinoShelvingVideo from "@/assets/dino-shelving.mp4";
import dinoOrganizingVideo from "@/assets/dino-organizing.mp4";

interface DinoLibrarianProps {
  pose: 'shelving' | 'organizing' | 'reading' | 'stamping' | 'tidying';
  size?: number;
  className?: string;
}

export function DinoLibrarian({ pose, size = 200, className }: DinoLibrarianProps) {
  return (
    <div className={cn("transition-all duration-700 ease-in-out", className)} style={{ width: size, height: size }}>
      {pose === 'shelving' && <ShelvingPose />}
      {pose === 'organizing' && <OrganizingPose />}
      {pose === 'reading' && <ReadingPose />}
      {pose === 'stamping' && <StampingPose />}
      {pose === 'tidying' && <TidyingPose />}
    </div>
  );
}

// Shelving Pose (0-20%): Dino reaching up to place books
function ShelvingPose() {
  return (
    <video
      src={dinoShelvingVideo}
      autoPlay
      loop
      muted
      playsInline
      className="w-full h-full object-contain"
    />
  );
}

// Organizing Pose (20-40%): Dino sorting books
function OrganizingPose() {
  return (
    <video
      src={dinoOrganizingVideo}
      autoPlay
      loop
      muted
      playsInline
      className="w-full h-full object-contain"
    />
  );
}

// Reading Pose (40-60%): Dino sitting with open book
function ReadingPose() {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Comfy chair */}
      <rect x="55" y="130" width="90" height="10" rx="5" fill="hsl(335, 42%, 40%)" opacity="0.3" />
      <rect x="50" y="100" width="10" height="40" rx="5" fill="hsl(335, 42%, 40%)" opacity="0.3" />
      <rect x="140" y="100" width="10" height="40" rx="5" fill="hsl(335, 42%, 40%)" opacity="0.3" />
      
      {/* Dino body (sitting) */}
      <ellipse cx="100" cy="115" rx="40" ry="35" fill="hsl(82, 60%, 34%)" />
      
      {/* Dino head */}
      <circle cx="100" cy="80" r="25" fill="hsl(82, 60%, 34%)" />
      
      {/* Eye (content) */}
      <circle cx="105" cy="78" r="4" fill="hsl(140, 35%, 17%)" />
      
      {/* Glasses */}
      <circle cx="105" cy="78" r="8" fill="none" stroke="hsl(140, 35%, 17%)" strokeWidth="1.5" />
      <circle cx="93" cy="78" r="8" fill="none" stroke="hsl(140, 35%, 17%)" strokeWidth="1.5" />
      <line x1="101" y1="78" x2="97" y2="78" stroke="hsl(140, 35%, 17%)" strokeWidth="1.5" />
      
      {/* Open book */}
      <path d="M 70 120 L 100 115 L 130 120 L 130 145 L 100 140 L 70 145 Z" fill="hsl(45, 100%, 95%)" stroke="hsl(140, 35%, 17%)" strokeWidth="1.5" />
      <line x1="100" y1="115" x2="100" y2="140" stroke="hsl(140, 35%, 17%)" strokeWidth="1.5" />
      <line x1="75" y1="125" x2="90" y2="123" stroke="hsl(140, 35%, 17%)" strokeWidth="0.8" opacity="0.5" />
      <line x1="75" y1="130" x2="90" y2="128" stroke="hsl(140, 35%, 17%)" strokeWidth="0.8" opacity="0.5" />
      <line x1="110" y1="123" x2="125" y2="125" stroke="hsl(140, 35%, 17%)" strokeWidth="0.8" opacity="0.5" />
      <line x1="110" y1="128" x2="125" y2="130" stroke="hsl(140, 35%, 17%)" strokeWidth="0.8" opacity="0.5" />
      
      {/* Legs (sitting) */}
      <rect x="75" y="140" width="12" height="20" rx="6" fill="hsl(82, 60%, 34%)" />
      <rect x="113" y="140" width="12" height="20" rx="6" fill="hsl(82, 60%, 34%)" />
      
      {/* Book spikes */}
      <rect x="88" y="95" width="6" height="10" fill="hsl(335, 42%, 40%)" rx="1" />
      <rect x="96" y="92" width="6" height="12" fill="hsl(45, 100%, 76%)" rx="1" />
      <rect x="104" y="95" width="6" height="10" fill="hsl(335, 42%, 40%)" rx="1" />
    </svg>
  );
}

// Stamping Pose (60-80%): Dino at desk with stamp
function StampingPose() {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Desk */}
      <rect x="40" y="125" width="120" height="8" fill="hsl(140, 35%, 17%)" opacity="0.2" />
      
      {/* Book on desk */}
      <rect x="110" y="110" width="35" height="18" fill="hsl(45, 100%, 76%)" rx="1" />
      
      {/* Dino body */}
      <ellipse cx="80" cy="105" rx="35" ry="38" fill="hsl(82, 60%, 34%)" />
      
      {/* Dino head */}
      <circle cx="85" cy="70" r="25" fill="hsl(82, 60%, 34%)" />
      
      {/* Eye (focused) */}
      <circle cx="90" cy="68" r="4" fill="hsl(140, 35%, 17%)" />
      
      {/* Glasses */}
      <circle cx="90" cy="68" r="8" fill="none" stroke="hsl(140, 35%, 17%)" strokeWidth="1.5" />
      <circle cx="78" cy="68" r="8" fill="none" stroke="hsl(140, 35%, 17%)" strokeWidth="1.5" />
      <line x1="86" y1="68" x2="82" y2="68" stroke="hsl(140, 35%, 17%)" strokeWidth="1.5" />
      
      {/* Arm with stamp */}
      <path d="M 90 90 Q 105 95 115 100" stroke="hsl(82, 60%, 34%)" strokeWidth="8" fill="none" strokeLinecap="round" />
      
      {/* Stamp */}
      <rect x="110" y="95" width="12" height="8" fill="hsl(335, 42%, 40%)" rx="1" />
      <rect x="112" y="88" width="8" height="8" fill="hsl(140, 35%, 17%)" />
      
      {/* Stamp mark on book */}
      <circle cx="127" cy="119" r="5" fill="hsl(335, 42%, 40%)" opacity="0.4" />
      
      {/* Legs */}
      <rect x="65" y="135" width="12" height="25" rx="6" fill="hsl(82, 60%, 34%)" />
      <rect x="85" y="135" width="12" height="25" rx="6" fill="hsl(82, 60%, 34%)" />
      
      {/* Book spikes */}
      <rect x="68" y="90" width="6" height="10" fill="hsl(45, 100%, 76%)" rx="1" />
      <rect x="76" y="87" width="6" height="12" fill="hsl(335, 42%, 40%)" rx="1" />
      <rect x="84" y="90" width="6" height="10" fill="hsl(45, 100%, 76%)" rx="1" />
    </svg>
  );
}

// Tidying Pose (80-100%): Dino with broom
function TidyingPose() {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Dust particles */}
      <circle cx="130" cy="150" r="2" fill="hsl(140, 35%, 17%)" opacity="0.2" />
      <circle cx="140" cy="155" r="1.5" fill="hsl(140, 35%, 17%)" opacity="0.2" />
      <circle cx="135" cy="145" r="1.8" fill="hsl(140, 35%, 17%)" opacity="0.2" />
      
      {/* Dino body */}
      <ellipse cx="90" cy="115" rx="35" ry="40" fill="hsl(82, 60%, 34%)" />
      
      {/* Dino head */}
      <circle cx="90" cy="80" r="25" fill="hsl(82, 60%, 34%)" />
      
      {/* Eye (satisfied) */}
      <circle cx="95" cy="78" r="4" fill="hsl(140, 35%, 17%)" />
      
      {/* Glasses */}
      <circle cx="95" cy="78" r="8" fill="none" stroke="hsl(140, 35%, 17%)" strokeWidth="1.5" />
      <circle cx="83" cy="78" r="8" fill="none" stroke="hsl(140, 35%, 17%)" strokeWidth="1.5" />
      <line x1="91" y1="78" x2="87" y2="78" stroke="hsl(140, 35%, 17%)" strokeWidth="1.5" />
      
      {/* Arms holding broom */}
      <path d="M 100 100 Q 115 120 125 140" stroke="hsl(82, 60%, 34%)" strokeWidth="8" fill="none" strokeLinecap="round" />
      
      {/* Broom handle */}
      <line x1="125" y1="140" x2="145" y2="170" stroke="hsl(140, 35%, 17%)" strokeWidth="3" />
      
      {/* Broom bristles */}
      <path d="M 140 168 L 150 172 M 142 169 L 152 173 M 144 170 L 154 174" stroke="hsl(45, 100%, 76%)" strokeWidth="2" strokeLinecap="round" />
      
      {/* Legs */}
      <rect x="75" y="145" width="12" height="25" rx="6" fill="hsl(82, 60%, 34%)" />
      <rect x="95" y="145" width="12" height="25" rx="6" fill="hsl(82, 60%, 34%)" />
      
      {/* Book spikes */}
      <rect x="78" y="100" width="6" height="10" fill="hsl(335, 42%, 40%)" rx="1" />
      <rect x="86" y="97" width="6" height="12" fill="hsl(45, 100%, 76%)" rx="1" />
      <rect x="94" y="100" width="6" height="10" fill="hsl(335, 42%, 40%)" rx="1" />
    </svg>
  );
}
